"use client";
import { useState } from "react";
import CommonCard from "../../../global/card";
import CommonTabs from "../../../global/tabs";
import { Form, Row, Col, Input, message, Button, Card, Upload } from "antd";
import { useGenericQuery } from "../../../../queries/use-generic-query";
import { useGenericMutation } from "../../../../queries/use-generic-mutation";
import { QUERY_KEYS } from "../../../../lib/query-constants";
import CommonSelect from "../../../global/select";
import CommonCheckbox from "../../../global/checkbox";
import { capitalizeFirstLetter } from "../../../../utils/functions";
import { InboxOutlined, FileTextOutlined, DownloadOutlined, ImportOutlined } from "@ant-design/icons";

const { Dragger } = Upload;

const EnhancedLeadImportViaCSV = () => {
    const [form] = Form.useForm();
    const [activeTab, setActiveTab] = useState("1");
    const [csvFile, setCsvFile] = useState(null);
    const [destinationId, setDestinationId] = useState([]);
    const [autoResponderMessage, setAutoResponderMessage] = useState(null);
    const [autoresponderId, setAutoResponderId] = useState("");

    // Fetch Destinations
    const { data: destinations, isLoading: destinationsLoading } = useGenericQuery({
        queryKey: QUERY_KEYS.destinations.all,
        endpoint: "integration",
    });

    // Fetch Autoresponders
    const { data: autoresponders, isLoading: autorespondersLoading } = useGenericQuery({
        queryKey: ["autoresponder"],
        endpoint: "autoresponder",
    });

    // Import Mutation
    const { mutate: importLeads, isPending: isImporting } = useGenericMutation({
        endpoint: "leadimportcsv",
        onSuccess: () => {
            message.success("Leads import started successfully.");
            setCsvFile(null);
            setDestinationId([]);
            setAutoResponderId("");
            setAutoResponderMessage(null);
            form.resetFields();
        },
        onError: (err) => {
            message.error(err?.message || "Failed to start import.");
        }
    });

    const handleAutoResponderChange = (val) => {
        setAutoResponderId(val);
        if (val && autoresponders) {
            const selected = autoresponders.find(a => a._id === val);
            setAutoResponderMessage(selected?.message || null);
        } else {
            setAutoResponderMessage(null);
        }
    };

    const handleDestinationChange = (id, checked) => {
        if (checked) {
            setDestinationId(prev => [...prev, id]);
        } else {
            setDestinationId(prev => prev.filter(d => d !== id));
        }
    };

    const draggerProps = {
        name: "file",
        multiple: false,
        accept: ".csv",
        showUploadList: false,
        beforeUpload: (file) => {
            setCsvFile(file);
            return false; // Prevent automatic upload
        },
        onDrop(e) {
            console.log("Dropped files", e.dataTransfer.files);
        },
    };

    const handleSubmit = () => {
        if (!csvFile) {
            message.error("Please upload a CSV file.");
            return;
        }

        const formData = new FormData();
        formData.append("destinations", destinationId);
        formData.append("file", csvFile);
        formData.append("autoresponderId", autoresponderId);

        importLeads(formData);
    };

    const tabItems = [
        {
            key: "1",
            label: "Upload CSV File",
            children: (
                <div className="flex flex-col gap-3">
                    <div className="flex justify-end">
                        <a
                            href="/files/leads-sample.csv"
                            download
                            className="group flex items-center gap-3 px-3 py-2.5 bg-white border !border-primary/50 rounded-md hover:border-primary transition-all duration-300 !no-underline !text-inherit"
                            target="_blank"
                            rel="noreferrer"
                        >
                            <div className="bg-primary/10 text-primary size-8 flex justify-center items-center rounded-lg group-hover:!bg-primary group-hover:!text-white transition-colors duration-300">
                                <DownloadOutlined className="text-lg" />
                            </div>
                            <div className="flex flex-col">
                                <span className="font-semibold text-gray-700 group-hover:text-primary transition-colors">Download Sample CSV</span>
                                <span className="text-xs text-gray-500">Use this template for your import</span>
                            </div>
                        </a>
                    </div>
                    <div className="bg-gray-50 p-6 rounded-lg border border-dashed border-gray-300">
                        <Dragger
                            {...draggerProps}
                            className="
                                    group
                                    [&_.ant-upload-drag]:border
                                    [&_.ant-upload-drag]:border-dashed
                                    [&_.ant-upload-drag]:border-gray-300
                                    [&_.ant-upload-drag]:rounded-lg
                                    [&_.ant-upload-drag:hover]:!border-primary
                                    transition-all
                                "
                        >
                            <div className="flex justify-center mb-3">
                                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center text-primary text-2xl group-hover:bg-primary group-hover:!text-white transition-colors duration-300">
                                    <InboxOutlined />
                                </div>
                            </div>
                            <p className="ant-upload-text font-medium text-lg text-gray-800">
                                Click or drag CSV file to this area to upload
                            </p>
                            <p className="ant-upload-hint text-gray-500 mt-2">
                                Support for a single CSV file upload.
                            </p>
                        </Dragger>

                        {csvFile && (
                            <div className="mt-4 p-3 bg-blue-50 border border-blue-100 rounded-md flex items-center gap-3">
                                <FileTextOutlined className="text-xl text-primary" />
                                <div>
                                    <p className="text-sm font-semibold text-gray-800">{csvFile.name}</p>
                                    <p className="text-xs text-gray-500">{(csvFile.size / 1024).toFixed(2)} KB</p>
                                </div>
                                <Button
                                    type="text"
                                    danger
                                    className="ml-auto"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setCsvFile(null);
                                    }}
                                >
                                    Remove
                                </Button>
                            </div>
                        )}
                    </div>


                </div>
            )
        },
        {
            key: "2",
            label: "Choose Autoresponder",
            children: (
                <div className="p-4 border rounded-lg bg-gray-50 flex flex-col gap-4">
                    <div className="w-full md:w-1/2">
                        <label className="block text-sm font-medium mb-1">Select Autoresponder</label>
                        <CommonSelect
                            placeholder="Select One"
                            options={autoresponders?.map(a => ({ label: a.name, value: a._id })) || []}
                            value={autoresponderId || undefined}
                            onChange={handleAutoResponderChange}
                            loading={autorespondersLoading}
                        />
                    </div>
                    {autoResponderMessage && (
                        <Card size="small" title="Message Preview" className="mt-2">
                            <div dangerouslySetInnerHTML={{ __html: autoResponderMessage }} />
                        </Card>
                    )}
                </div>
            )
        },
        {
            key: "3",
            label: "Choose Destination(s)",
            children: (
                <div className="p-4 border rounded-lg bg-gray-50">
                    {destinationsLoading ? (
                        <div className="text-center p-4">Loading destinations...</div>
                    ) : (
                        <Row gutter={[16, 16]}>
                            {destinations?.map((des) => (
                                <Col span={12} key={des.integrationCompany}>
                                    <div className="flex items-center gap-2">
                                        <CommonCheckbox
                                            checked={destinationId.includes(des.integrationCompany)}
                                            onChange={(e) => handleDestinationChange(des.integrationCompany, e.target.checked)}
                                        />
                                        <span className="text-sm">{capitalizeFirstLetter(des.integrationCompany)}</span>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    )}
                </div>
            )
        }
    ];

    return (
        <CommonCard>
            < CommonTabs
                items={tabItems}
                activeKey={activeTab}
                onChange={setActiveTab}
            />
            <div className="mt-6 flex justify-start">
                <Button
                    type="primary"
                    icon={<ImportOutlined />}
                    onClick={handleSubmit}
                    loading={isImporting}
                    size="large"
                    className="!bg-primary !text-white rounded-md"
                >
                    Start Import
                </Button>
            </div>
        </CommonCard >
    );
};

export default EnhancedLeadImportViaCSV;